import {
  isPatient,
  isTherapist,
  isAdmin,
  isSuperAdmin,
  isDPO,
  isSiteManager,
  roles,
  isDataScientist,
  isAnalyticsUser,
  isPublisher,
  isAccountExecutive,
  isAdminManager,
  isDataExportManager,
  isOptionalFeatureManager,
  isLicenseManager,
  isBusinessAnalyst,
  competences,
} from "@/services/auth.js";
import {
  isLatestPossiblePrivacyPolicySigned,
  deserializeConsent,
} from "@/utils/signedDocuments.js";
import Site from "@/models/Site.model";
import { resetState } from "@/store/utils";
import { clearStores } from "@/store";
import {
  areCookiesAccepted,
  removeAccessTokenCookie,
  setAccessTokenCookie,
} from "@/utils/cookies.js";
import {
  removeLocalStorageRefreshToken,
  getLocalStorageStayLoggedIn,
} from "@/store/localStorage.js";
import { decodeJwtPayload } from "@/utils/jwt.js";

const initialState = () => {
  return {
    /**
     * JWT token
     */
    token: null,

    /**
     * Whether the cookies are accepted. It is used to broadcast the value
     * across components.
     */
    cookiesAccepted: false,

    /**
     * Whether to hide the cookie bar. It is used to broadcast the value
     * across components
     */
    hideCookieBar: false,

    /**
     * The roles the user has
     *
     * The API sends us a list, so let's save it as a list too, regardless of
     * the fact that for now in our code, you can only have one role
     */
    roles: [],

    /**
     * The competences the admin user has
     */
    competences: [],

    /**
     * The user profile of the logged-in user
     */
    user: null,
  };
};

export default {
  namespaced: true,

  state: initialState,
  mutations: {
    /**
     * Reset the store state
     * @param {object} state Module state
     */
    clearStore(state) {
      resetState(initialState, state);
    },

    /**
     * Reset all states including this one
     * @param {object} state Module state
     */
    clearAll(state) {
      resetState(initialState, state);

      clearStores();
      removeAccessTokenCookie();
      removeLocalStorageRefreshToken();
    },

    /**
     * Set the JWT access token
     * @param {object} state Vuex state
     * @param {string} token token
     */
    setAccessToken(state, token) {
      state.token = token;
      if (areCookiesAccepted()) {
        // Store the cookie as a session cookie
        // if we're not supposed to stay logged in
        const isSessionCookie = !getLocalStorageStayLoggedIn();
        setAccessTokenCookie(token, isSessionCookie);
      }
    },

    /**
     * Set whether the user accepted cookies
     * This is set by the cookies bar
     * @param {object} state Vuex state
     * @param {boolean} cookiesAccepted Whether the cookies were accepted
     */
    setCookiesAccepted(state, cookiesAccepted) {
      state.cookiesAccepted = !!cookiesAccepted;
    },

    /**
     * Hides the cookie bar
     * @param {object} state Vuex state
     * @param {boolean} hideCookieBar Whether the cookie bar should be hidden
     */
    setHideCookieBar(state, hideCookieBar) {
      state.hideCookieBar = !!hideCookieBar;
    },

    /**
     * Set the roles of the user
     * Do some validation of the role strings sent by the API
     * @param {object} state Vuex state
     * @param {array} roles List of roles
     */
    setRoles(state, roles) {
      state.roles = roles;
    },

    /**
     * Set the competences of the user
     * @param {object} state Vuex state
     * @param {array} competences List of roles
     */
    setCompetences(state, competences) {
      state.competences = competences;
    },

    /**
     * Set the user object
     * @param {object} user The user object
     */
    setUserObject(state, user) {
      state.user = user;
    },

    addSignedDocument(state, newDocument) {
      if (!state.user.signedDocuments) {
        state.user.signedDocuments = [];
      }
      state.user.signedDocuments.push(newDocument);
    },
  },

  actions: {
    setAccessToken({ commit }, token) {
      // Prevents storing undefined or null as a string
      if (!token) {
        token = "";
      }

      commit("setAccessToken", token);
    },

    setCookiesAccepted({ commit }, cookiesAccepted) {
      commit("setCookiesAccepted", !!cookiesAccepted);
    },

    setHideCookieBar({ commit }, hideCookieBar) {
      commit("setHideCookieBar", !!hideCookieBar);
    },

    setRoles({ commit }, newState) {
      if (!newState) {
        throw `invalid roles given: ${newState}`;
      }

      for (const role of newState) {
        if (roles.indexOf(role) === -1) {
          // something has gone wrong: the API sent us a role we don't understand
          throw `invalid Role found: ${role}`;
        }
      }
      commit("setRoles", newState);
    },

    setCompetences({ commit }, newState) {
      if (!newState) {
        return;
      }

      // the API may send competences this frontend version does not know yet
      // (e.g. a newer backend); unknown competences grant nothing in the UI,
      // so ignore them instead of failing the login
      const unknownCompetences = newState.filter(
        (competence) => competences.indexOf(competence) === -1
      );
      if (unknownCompetences.length > 0) {
        console.warn(
          `ignoring unknown competences: ${unknownCompetences.join(", ")}`
        );
      }

      commit(
        "setCompetences",
        newState.filter((competence) => competences.indexOf(competence) !== -1)
      );
    },

    /**
     * Deletes all user related data, including authentication tokens
     */
    clearAll({ commit }) {
      commit("clearAll");
    },

    setUserObject({ commit, dispatch }, newState) {
      commit("setUserObject", newState);
      dispatch("Language/setLanguage", newState.language, { root: true });
    },
    addSignedDocument({ commit }, newDocument) {
      commit("addSignedDocument", newDocument);
    },
    clearStore({ commit }) {
      commit("clearStore");
    },
  },
  getters: {
    /**
     * Whether the user has an access token
     * @param {object} state Module state
     */
    hasAccessToken(state) {
      return !!state.token;
    },

    /**
     * The application user id of the currently logged-in user, decoded from
     * the access token. Used to detect when a link meant for a different
     * account is opened while this session is still active (e.g. e-mail
     * change verification links).
     * @param {object} state Module state
     */
    loggedInUserId(state) {
      return decodeJwtPayload(state.token)?.id ?? null;
    },

    /**
     * Whether the user needs to sign the privacy policy
     * @param state
     * @param getters
     */
    isPrivacyPolicySignatureRequired(state, getters) {
      // The role comes from state.roles, which is always a list: a missing
      // profile can't be mistaken for a signed document
      if (!getters.isMindMotionUser) {
        return false;
      }

      // Otherwise check the current user document status
      return !isLatestPossiblePrivacyPolicySigned(
        state.user?.signedDocuments || []
      );
    },

    /**
     * Whether the user needs to sign the consent form
     * @param state
     * @param getters
     */
    isConsentFormSignatureRequired(state, getters) {
      // Don't check the consent form for non-patients
      if (!getters.isPatient) {
        return false;
      }
      // Use deserializeConsent to get the consent object
      const consents = deserializeConsent(state.user?.signedDocuments || []);
      // If the first consent (checkbox 0) is false, signature is required
      return !consents[0];
    },

    consents(state) {
      const consents = deserializeConsent(state.user?.signedDocuments || []);
      return consents;
    },

    /**
     * Whether the user is a MindMotion user (Therapist, Patient or SiteManager)
     * @param {object} state Module state
     */
    isMindMotionUser(state) {
      return (
        isPatient(state.roles) ||
        isTherapist(state.roles) ||
        isSiteManager(state.roles)
      );
    },

    isPatient(state) {
      return isPatient(state.roles);
    },

    isTherapist(state) {
      return isTherapist(state.roles);
    },

    isAdmin(state) {
      return isAdmin(state.roles);
    },

    isSuperAdmin(state) {
      return isSuperAdmin(state.roles);
    },

    isDPO(state) {
      return isDPO(state.roles);
    },

    isSiteManager(state) {
      return isSiteManager(state.roles);
    },

    isSuperTherapist(state) {
      if (!state.user) {
        return false;
      }
      return state.user.isSuperTherapist && isTherapist(state.roles);
    },

    username(state) {
      if (!state.user) {
        return null;
      }
      return state.user.userName;
    },

    siteId(state) {
      if (!state.user) {
        return null;
      }
      return state.user.siteId;
    },

    /**
     * Whether the current user must confirm key profile actions (e.g.: change password or email) with his current password
     * N.B.: Must match UserManagementService.IsPasswordRequiredForUsername in the backend
     * @param state
     * @param getters
     */
    isPasswordRequired(state, getters) {
      if (
        !getters.isMindMotionUser ||
        getters.isPatient ||
        state.user?.enforceSAMLLogin !== true
      ) {
        return true;
      }

      // Unloaded site is considered as SAML disabled: asking the password is safer
      return Site.query().find(getters.siteId)?.isSAMLEnabled !== true;
    },

    /**
     * Check the competences of admins
     */
    isDataScientistAdmin(state) {
      return isAdmin(state.roles) && isDataScientist(state.competences);
    },
    isAnalyticsUserAdmin(state) {
      return isAdmin(state.roles) && isAnalyticsUser(state.competences);
    },
    isPublisherAdmin(state) {
      return isAdmin(state.roles) && isPublisher(state.competences);
    },
    isAccountExecutiveAdmin(state) {
      return isAdmin(state.roles) && isAccountExecutive(state.competences);
    },
    isAdminManagerAdmin(state) {
      return isAdmin(state.roles) && isAdminManager(state.competences);
    },
    isDataExportManagerAdmin(state) {
      return isAdmin(state.roles) && isDataExportManager(state.competences);
    },
    isOptionalFeatureManagerAdmin(state) {
      return (
        isAdmin(state.roles) && isOptionalFeatureManager(state.competences)
      );
    },
    isLicenseManagerAdmin(state) {
      return isAdmin(state.roles) && isLicenseManager(state.competences);
    },
    isBusinessAnalystAdmin(state) {
      return isAdmin(state.roles) && isBusinessAnalyst(state.competences);
    },
  },
};
